import React, {
  type Dispatch,
  type SetStateAction,
  useEffect,
  useMemo,
  type JSX,
} from 'react';
import EnhancedMarkdown from '@resources/js/components/EnhancedMarkdown/EnhancedMarkdown';
import { IconDefaultsContextProvider } from '@resources/js/contexts/useIconDefaults';
import VerticalContent from '@resources/js/components/VerticalContent/VerticalContent';
import Table, {
  AscendingSortIcon,
  type TableColumn,
  type TableProps,
} from '@resources/js/components/Table/Table';
import HorizontalContent from '@resources/js/components/HorizontalContent/HorizontalContent';
import { type CellProps, type ColumnInstance } from 'react-table';
import Button from '@resources/js/components/Button/Button';
import Bar from '@resources/js/components/Bar/Bar';
import Bars from '@resources/js/components/Bars/Bars';
import { type BuildsThroughputSectionViewModel } from '@resources/js/components/BuildsThroughputSection/BuildsThroughputSection';
import useThroughputBreakdownHiddenColumns from '@resources/js/contexts/useThroughputBreakdownHiddenColumns';
import useTableHiddenColumns, {
  TableHiddenColumnsProvider,
} from '@resources/js/contexts/useTableHiddenColumns';
import { type LinkViewModel } from '@resources/js/typings/viewModels/linkViewModel';
import DefaultLinkWrapper, {
  type LinkWrapper,
} from '@resources/js/components/LinkWrapper/LinkWrapper';
import Heading from '@resources/js/components/Heading/Heading';
import Tooltip from '@resources/js/components/Tooltip/Tooltip';

export type ThroughputBreakdownViewModel = {
  title: string | null;
  description: string;
  link: LinkViewModel | null;
  table: {
    columns: Record<ColumnKey, TableColumn | null | undefined>;
    data: Row[];
    footer: Row;
  } | null;
  limit?: number;
  hideableColumns: ColumnKey[];
  showAllColumnsLabel: string;
  fightExploitDescriptions: string[];
  fightExploitCssClass: string | null;
  stealthModeMessage?: string | null;
  LinkWrapper: LinkWrapper;
};

const iconDefaults = {
  ability: { bordered: true },
  actor: { bordered: true, rounded: false },
  icon: { bordered: true, rounded: false },
};

export default function ThroughputBreakdown({
  title,
  description,
  link,
  table,
  limit,
  hideableColumns,
  showAllColumnsLabel,
  fightExploitDescriptions,
  fightExploitCssClass,
  stealthModeMessage,
  LinkWrapper = DefaultLinkWrapper,
}: ThroughputBreakdownViewModel): JSX.Element {
  return (
    <div className={'throughput-breakdown'}>
      {title && (
        <Heading level={3}>
          <EnhancedMarkdown markdown={title} wrapped={false} />
        </Heading>
      )}
      <div className={'hide-on-compact'}>
        <div className={'throughput-breakdown__description-and-link'}>
          <div>
            {description && (
              <EnhancedMarkdown markdown={description} wrapped={false} />
            )}
          </div>
          {link && (
            <LinkWrapper
              href={link.url}
              target={link.isExternal ? '_blank' : undefined}
              className={'throughput-breakdown__link'}
            >
              {link.label}
            </LinkWrapper>
          )}
        </div>
      </div>
      {stealthModeMessage && (
        <div className={'throughput-breakdown__exploit-details banned'}>
          {stealthModeMessage}
        </div>
      )}
      {fightExploitDescriptions.length > 0 && (
        <VerticalContent
          className={`throughput-breakdown__exploit-details ${fightExploitCssClass}`}
        >
          {fightExploitDescriptions.map((description, index) => (
            <div key={index}>
              <EnhancedMarkdown markdown={description} wrapped={false} />
            </div>
          ))}
        </VerticalContent>
      )}
      <IconDefaultsContextProvider overrides={iconDefaults}>
        <TableHiddenColumnsProvider>
          <BreakdownTable
            table={table}
            limit={limit}
            hideableColumns={hideableColumns}
            showAllColumnsLabel={showAllColumnsLabel}
            LinkWrapper={LinkWrapper}
          />
        </TableHiddenColumnsProvider>
      </IconDefaultsContextProvider>
    </div>
  );
}

type ColumnKey =
  | 'parse'
  | 'name'
  | 'nameUrl'
  | 'amountPercent'
  | 'amountBar'
  | 'amountTotal'
  | 'absorbed'
  | 'casts'
  | 'castsPerMinute'
  | 'averageCast'
  | 'hits'
  | 'averageHit'
  | 'critPercent'
  | 'multistrikePercent'
  | 'uptimePercent'
  | 'missPercent'
  | 'overhealPercent'
  | 'mitigatedPercent'
  | 'itemLevel'
  | 'amountPerSecond';

type Row = {
  parse: string;
  parseTooltip: string;
  name: string;
  nameUrl: string | null;
  amountPercent: number;
  amountBar: number;
  barOnePercent: number;
  barTwoPercent: number;
  amountTotal: number;
  absorbed: number;
  casts: number;
  castsPerMinute: number;
  averageCast: number;
  hits?: number;
  averageHit: number;
  critPercent: number;
  multistrikePercent: number;
  uptimePercent: number;
  missPercent: number;
  overhealPercent: number;
  mitigatedPercent: number;
  itemLevel?: number | string;
  amountPerSecond: number;
  amountType: number;
  averageHitCast: number;
  averageTickCast: number;
  tickHits?: number;
  averageHitHit?: number;
  averageTickHit?: number;
  tickCritPercent: number;
  tickMultistrikePercent: number;
  tickMissPercent: number;
  exploitDescription: string | null;
  exploitCssClass: string | null;
};

function BreakdownTable({
  table,
  limit,
  hideableColumns,
  showAllColumnsLabel,
  LinkWrapper = DefaultLinkWrapper,
}: {
  table: BuildsThroughputSectionViewModel['breakdowns'][0]['table'];
  limit?: number;
  hideableColumns: ColumnKey[];
  showAllColumnsLabel: string;
  LinkWrapper?: LinkWrapper;
}): JSX.Element {
  const columns = useMemo(
    () =>
      Object.fromEntries(
        Object.entries(table?.columns ?? {}).map(([accessor, column]) => [
          accessor,
          {
            ...column,
            renderCell: (cell) => renderCell(cell, LinkWrapper),
            renderFooter,
          } as TableColumn<Row>,
        ]),
      ),
    [table?.columns, LinkWrapper],
  );

  const truncate: TableProps['truncate'] = {
    showMoreButton: { styles: 'inline-row' },
  };

  if (limit) {
    truncate.limit = limit;
  }

  const { showAllColumns, setShowAllColumns } =
    useThroughputBreakdownHiddenColumns();
  const { setHiddenColumns } = useTableHiddenColumns();
  useEffect(() => {
    setHiddenColumns(showAllColumns ? [] : hideableColumns);
  }, [hideableColumns, setHiddenColumns, showAllColumns]);

  return (
    <VerticalContent gap={'smaller'}>
      {table && (
        <Table
          style={'report'}
          {...table}
          columns={columns}
          truncate={truncate}
          getRowClassName={(row) =>
            table.data[row.index]?.exploitCssClass ?? undefined
          }
        />
      )}
      {hideableColumns.length > 0 && (
        <div style={{ display: 'flex' }}>
          <ShowAllColumnsToggle
            showAllColumns={showAllColumns}
            setShowAllColumns={setShowAllColumns}
            showAllColumnsLabel={showAllColumnsLabel}
          />
        </div>
      )}
    </VerticalContent>
  );
}

function ShowAllColumnsToggle({
  showAllColumns,
  setShowAllColumns,
  showAllColumnsLabel,
}: {
  showAllColumns: boolean;
  setShowAllColumns: Dispatch<SetStateAction<boolean>>;
  showAllColumnsLabel: string;
}): JSX.Element {
  return (
    <label>
      <HorizontalContent gap={'tiny'}>
        <input
          type={'checkbox'}
          checked={showAllColumns}
          onChange={() => setShowAllColumns((prev) => !prev)}
        />
        {showAllColumnsLabel}
      </HorizontalContent>
    </label>
  );
}

// TODO relocate to shared location
export const formatValue = (value: number | '-') => {
  if (value === '-' || (Number.isInteger(value) && Math.abs(value) < 10000)) {
    return value;
  }

  if (Math.abs(value) > 1_000_000) {
    return new Intl.NumberFormat('en-US', {
      notation: 'compact',
      compactDisplay: 'short',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  }

  return new Intl.NumberFormat('en-US', {
    notation: 'compact',
    compactDisplay: 'short',
    minimumFractionDigits: 1,
    maximumFractionDigits: 1,
  }).format(value);
};

const dashIfZero = (value: number) => (value === 0 ? '-' : value);

const maybeWithParenthesis = (
  value: string | number,
  secondaryValue: string | number,
) => {
  if (secondaryValue && secondaryValue !== '-') {
    if (value && value !== '-') {
      return `${value} (${secondaryValue})`;
    } else {
      return secondaryValue;
    }
  }

  return value;
};

const formatPercent = (value: number | '-', fractionDigits = 2) => {
  return value === '-' ? value : value.toFixed(fractionDigits) + '%';
};

const renderCell = (cell: CellProps<Row>, LinkWrapper?: LinkWrapper) =>
  renderTableValue(
    cell.row.original,
    cell.column.id as ColumnKey,
    // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
    cell.row.canExpand,
    // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
    cell.row.isExpanded,
    // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
    cell.row.toggleRowExpanded,
    // @ts-expect-error TODO: Migrate to @tanstack/react-table for proper typing
    cell.row.depth,
    false,
    LinkWrapper,
  );

const renderFooter = (column: ColumnInstance<Row>, footer?: Row) =>
  footer
    ? renderTableValue(
        footer,
        column.id as ColumnKey,
        false,
        false,
        () => {},
        0,
        true,
      )
    : null;

const renderTableValue = (
  row: Row,
  accessor: ColumnKey,
  canExpand: boolean,
  isExpanded: boolean,
  toggleRowExpanded: (isExpanded: boolean) => void,
  expansionDepth: number,
  isFooter = false,
  LinkWrapper: LinkWrapper = DefaultLinkWrapper,
) => {
  if (row[accessor] === null) {
    return null;
  }

  switch (accessor) {
    case 'parse': {
      const content = <EnhancedMarkdown markdown={row.parse} />;
      if (row.parseTooltip) {
        return (
          <Tooltip
            content={row.parseTooltip}
            offsetOptions={{ mainAxis: 10 }}
            placementOptions={{ allowedPlacements: ['top'] }}
            includeArrow={false}
          >
            {content}
          </Tooltip>
        );
      }

      return content;
    }
    case 'name': {
      let nameNode: JSX.Element = <EnhancedMarkdown markdown={row.name} />;

      if (row.nameUrl) {
        nameNode = <LinkWrapper href={row.nameUrl}>{nameNode}</LinkWrapper>;
      }

      let child;
      if (canExpand) {
        child = (
          <HorizontalContent style={{ justifyContent: 'space-between' }}>
            {nameNode}
            <Button
              styles={['unstyled']}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                toggleRowExpanded(!isExpanded);
              }}
            >
              <AscendingSortIcon style={'report'} />
            </Button>
          </HorizontalContent>
        );
      } else {
        child = nameNode;
      }

      const nameContent = (
        <div style={{ paddingLeft: expansionDepth * 26 }}>{child}</div>
      );

      if (row.exploitDescription) {
        return (
          <Tooltip
            content={
              <EnhancedMarkdown
                markdown={row.exploitDescription}
                wrapped={false}
              />
            }
            offsetOptions={{ mainAxis: 10 }}
            placementOptions={{ allowedPlacements: ['top'] }}
            includeArrow={false}
          >
            <HorizontalContent style={{ alignItems: 'center' }}>
              {nameContent}
              <i className={'zmdi zmdi-alert-circle'} />
            </HorizontalContent>
          </Tooltip>
        );
      }

      return nameContent;
    }
    case 'amountPercent':
      return formatPercent(row.amountPercent);
    case 'amountBar': {
      const type = row.amountType.toString();

      if (!row.barTwoPercent) {
        return <Bar type={type} percent={row.amountBar} />;
      }

      return (
        <Bars
          bars={[
            {
              type,
              percent: row.barOnePercent,
            },
            {
              type,
              percent: row.barTwoPercent,
              darken: true,
            },
          ]}
        />
      );
    }
    case 'amountTotal':
      return formatValue(row.amountTotal);
    case 'absorbed':
      return formatValue(row.absorbed);
    case 'casts':
      return formatValue(dashIfZero(row.casts));
    case 'castsPerMinute':
      return formatValue(dashIfZero(row.castsPerMinute));
    case 'averageCast':
      return maybeWithParenthesis(
        formatValue(dashIfZero(row.averageHitCast)),
        formatValue(dashIfZero(row.averageTickCast)),
      );
    case 'hits':
      return maybeWithParenthesis(
        formatValue(dashIfZero(row.hits ?? 0)),
        formatValue(dashIfZero(row.tickHits ?? 0)),
      );
    case 'averageHit':
      return maybeWithParenthesis(
        formatValue(dashIfZero(row.averageHitHit ?? 0)),
        formatValue(dashIfZero(row.averageTickHit ?? 0)),
      );
    case 'critPercent':
      return maybeWithParenthesis(
        formatPercent(dashIfZero(row.critPercent), 1),
        formatPercent(dashIfZero(row.tickCritPercent), 1),
      );
    case 'multistrikePercent':
      return maybeWithParenthesis(
        formatPercent(dashIfZero(row.multistrikePercent), 1),
        formatPercent(dashIfZero(row.tickMultistrikePercent), 1),
      );
    case 'uptimePercent':
      return formatPercent(dashIfZero(row.uptimePercent));
    case 'missPercent':
      return maybeWithParenthesis(
        formatPercent(dashIfZero(row.missPercent)),
        formatPercent(dashIfZero(row.tickMissPercent)),
      );
    case 'overhealPercent':
      return formatPercent(dashIfZero(row.overhealPercent));
    case 'mitigatedPercent':
      return formatPercent(dashIfZero(row.mitigatedPercent));
    case 'itemLevel':
      return formatValue(dashIfZero(Number(row.itemLevel ?? 0)));
    case 'amountPerSecond':
      return isFooter ? (
        formatValue(row.amountPerSecond)
      ) : (
        <span className={'primary'}>{formatValue(row.amountPerSecond)}</span>
      );
    default:
      return row[accessor];
  }
};
